import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { ArrowLeft, Zap, Check, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { RideMap } from "@/components/RideMap";
import { BottomNav } from "@/components/BottomNav";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/puerto/$id")({
  head: () => ({
    meta: [
      { title: "Ficha de puerto — Watts" },
      { name: "description", content: "Datos, mapa y verificación del puerto." },
      { property: "og:title", content: "Ficha de puerto — Watts" },
      { property: "og:description", content: "Datos, mapa y verificación del puerto." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PuertoDetalle,
});

interface Port {
  id: string;
  name: string;
  category: string | null;
  comarca: string | null;
  province: string;
  slope_side: string | null;
  altitude_m: number | null;
  length_km: number | null;
  elevation_gain_m: number | null;
  avg_gradient: number | null;
  max_gradient: number | null;
  watts_reward: number | null;
  profile_url: string | null;
  lat: number | null;
  lng: number | null;
  start_lat: number | null;
  start_lng: number | null;
}

type CompletionStatus = "approved" | "in_review" | null;

function PuertoDetalle() {
  const { id } = Route.useParams();
  const router = useRouter();
  const [port, setPort] = useState<Port | null>(null);
  const [busy, setBusy] = useState(true);
  const [status, setStatus] = useState<CompletionStatus>(null);
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadStatus = useCallback(async () => {
    const { data: sess } = await supabase.auth.getUser();
    const uid = sess.user?.id;
    if (!uid) { setStatus(null); return; }
    const { data } = await supabase
      .from("port_completions")
      .select("status")
      .eq("port_id", id)
      .eq("user_id", uid)
      .in("status", ["approved", "in_review"])
      .maybeSingle();
    setStatus((data?.status as CompletionStatus) ?? null);
  }, [id]);

  useEffect(() => {
    (async () => {
      setBusy(true);
      const { data } = await supabase
        .from("ports")
        .select("id, name, category, comarca, province, slope_side, altitude_m, length_km, elevation_gain_m, avg_gradient, max_gradient, watts_reward, profile_url, lat, lng, start_lat, start_lng")
        .eq("id", id)
        .single();
      setPort(data as Port | null);
      await loadStatus();
      setBusy(false);
    })();
  }, [id, loadStatus]);

  const meta = port
    ? [port.comarca, port.province, port.slope_side].filter(Boolean).join(" · ")
    : "";

  const mapPosition: [number, number] | null =
    port?.lat != null && port?.lng != null
      ? [port.lat, port.lng]
      : port?.start_lat != null && port?.start_lng != null
        ? [port.start_lat, port.start_lng]
        : null;

  const fmt = (v: number | null | undefined, suffix = "") =>
    v == null ? "—" : `${v}${suffix}`;

  const submit = async () => {
    const trimmed = url.trim();
    if (!/^https?:\/\/.+/i.test(trimmed)) {
      toast.error("Pega un enlace válido de Instagram");
      return;
    }
    setSubmitting(true);
    const { data: sess } = await supabase.auth.getUser();
    const uid = sess.user?.id;
    if (!uid) {
      setSubmitting(false);
      toast.error("Debes iniciar sesión");
      return;
    }
    const { error } = await supabase.from("port_completions").insert({
      user_id: uid,
      port_id: id,
      status: "in_review",
      instagram_url: trimmed,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Solicitud enviada");
    setOpen(false);
    setUrl("");
    setStatus("in_review");
  };

  return (
    <>
      <div className="h-screen overflow-y-auto bg-background fade-in pb-24">
        <header className="flex items-center gap-3 px-5 pt-[max(env(safe-area-inset-top),1rem)] pb-4">
          <button
            onClick={() => router.history.back()}
            className="press-scale flex h-10 w-10 items-center justify-center rounded-full bg-surface text-foreground"
            aria-label="Volver"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-2xl text-foreground truncate">
              {port?.name ?? (busy ? "Cargando…" : "Puerto")}
            </h1>
            {port?.category && (
              <div className="text-sm text-primary font-semibold">{port.category}</div>
            )}
          </div>
        </header>

        <main className="px-4 space-y-6">
          {meta && (
            <p className="px-1 text-sm text-muted-foreground">{meta}</p>
          )}

          {port && (
            <section className="grid grid-cols-2 gap-3">
              <DataCell label="Altitud" value={fmt(port.altitude_m, " m")} />
              <DataCell label="Longitud" value={fmt(port.length_km, " km")} />
              <DataCell label="Desnivel" value={fmt(port.elevation_gain_m, " m")} />
              <DataCell label="Pendiente media" value={fmt(port.avg_gradient, " %")} />
              <DataCell label="Pendiente máxima" value={fmt(port.max_gradient, " %")} />
              <DataCell
                label="Watts al coronar"
                value={
                  <span className="inline-flex items-center gap-1 text-primary">
                    <Zap className="h-5 w-5" fill="currentColor" strokeWidth={0} />
                    {port.watts_reward ?? 0}
                  </span>
                }
                highlight
              />
            </section>
          )}

          {port && mapPosition && (
            <section className="overflow-hidden rounded-2xl border border-border">
              <div className="h-56">
                <RideMap
                  rides={[{ id: port.id, modality: "road", lat: mapPosition[0], lng: mapPosition[1] }]}
                  center={mapPosition}
                  zoom={13}
                  interactive={false}
                />
              </div>
            </section>
          )}

          <section className="space-y-3">
            {status === "approved" ? (
              <div className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border border-primary/40 bg-primary/10 py-4 font-display text-lg font-bold uppercase tracking-wide text-primary">
                <Check className="h-5 w-5" /> Coronado
              </div>
            ) : status === "in_review" ? (
              <button
                disabled
                className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border border-border bg-surface py-4 font-display text-lg font-bold uppercase tracking-wide text-muted-foreground opacity-80"
              >
                <Clock className="h-5 w-5" /> En revisión
              </button>
            ) : (
              <button
                onClick={() => setOpen(true)}
                className="press-scale w-full rounded-2xl bg-primary py-4 font-display text-lg font-bold uppercase tracking-wide text-primary-foreground"
              >
                Solicitar verificación
              </button>
            )}
            {port?.profile_url && (
              <a
                href={port.profile_url}
                target="_blank"
                rel="noopener noreferrer"
                className="press-scale flex w-full items-center justify-center rounded-2xl border border-border bg-surface py-3 font-display text-base text-foreground"
              >
                Ver perfil de altimetría
              </a>
            )}
          </section>

          <p className="px-1 text-sm text-muted-foreground leading-relaxed">
            Sube tu foto en la cima a Instagram etiquetando <span className="text-foreground font-semibold">@watts</span> y pega el enlace. El equipo lo revisa y queda coronado.
          </p>
        </main>
      </div>
      <BottomNav />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="z-[10000]">
          <DialogHeader>
            <DialogTitle>Solicitar verificación</DialogTitle>
            <DialogDescription>
              Pega el enlace a tu publicación de Instagram con la foto en la cima.
            </DialogDescription>
          </DialogHeader>
          <input
            type="url"
            inputMode="url"
            autoFocus
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.instagram.com/p/…"
            className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <DialogFooter>
            <button
              onClick={() => setOpen(false)}
              className="rounded-xl border border-border bg-background px-4 py-2 text-sm text-foreground"
            >
              Cancelar
            </button>
            <button
              onClick={submit}
              disabled={submitting || !url.trim()}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {submitting ? "Enviando…" : "Enviar"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DataCell({
  label,
  value,
  highlight,
}: {
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <div className={`rounded-2xl border p-4 ${highlight ? "border-primary/40 bg-primary/5" : "border-border bg-surface"}`}>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl text-foreground">{value}</div>
    </div>
  );
}
